/*
  Just Tattoos — cart drawer.
  Plain ES2020, no dependencies, loaded with defer. Progressive: without it the
  add-to-cart forms post normally and the drawer stays closed.

  Responsibilities
    - open on the `cart:open` event; close on close button / backdrop / Escape;
      lock body scroll, trap focus, restore focus;
    - intercept every form[action="/cart/add"] submit, POST to /cart/add.js,
      refresh the drawer through the Section Rendering API and open it;
    - line quantity +/- and remove -> POST /cart/change.js { id, quantity };
    - keep [data-cart-count] in sync and emit `cart:updated`;
    - expose window.JT.cart.add(variantId, qty) for the PDP and quick-add;
    - product-card quantity steppers ([data-qty-*]) and the "Try On AR"
      control ([data-ar-try-on] -> emits `ar:open` with the product handle).
*/
(function () {
  'use strict';

  var doc = document;
  var body = doc.body;

  function one(selector, root) { return (root || doc).querySelector(selector); }
  function all(selector, root) {
    return Array.prototype.slice.call((root || doc).querySelectorAll(selector));
  }

  var JT = window.JT || {};

  function emit(name, detail) {
    if (typeof JT.emit === 'function') { JT.emit(name, detail); return; }
    doc.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
  }

  function money(cents) {
    if (typeof JT.formatMoney === 'function') return JT.formatMoney(cents);
    return '$' + (cents / 100).toFixed(2);
  }

  function jsonFetch(url, options) {
    return fetch(url, options).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          var err = new Error(data && data.description ? data.description : 'Request failed');
          err.description = data && data.description;
          err.status = response.status;
          throw err;
        }
        return data;
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Drawer element (kept in a variable because the Section Rendering API
   * replaces it on refresh).
   * ------------------------------------------------------------------ */
  var drawer = one('[data-cart-drawer]');
  var lastFocused = null;
  var refreshing = null;

  function isOpen() {
    return !!(drawer && drawer.getAttribute('data-open') === 'true');
  }

  function focusable(scope) {
    return all(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      scope
    );
  }

  function openCart() {
    if (!drawer) return;
    lastFocused = doc.activeElement;
    drawer.setAttribute('data-open', 'true');
    drawer.setAttribute('aria-hidden', 'false');
    body.classList.add('is-cart-open');
    var closeBtn = one('[data-cart-close]', drawer);
    var target = closeBtn || focusable(drawer)[0];
    if (target) target.focus();
  }

  function closeCart() {
    if (!drawer) return;
    drawer.setAttribute('data-open', 'false');
    drawer.setAttribute('aria-hidden', 'true');
    body.classList.remove('is-cart-open');
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  /* ------------------------------------------------------------------ *
   * Section refresh + cart count
   * ------------------------------------------------------------------ */
  function sectionUrl() {
    // Always render from the root: on product pages the Section Rendering API
    // returns an empty string for layout-level sections.
    var url = new URL((window.JT && JT.routes && JT.routes.root) || '/', window.location.origin);
    url.searchParams.set('sections', 'cart-drawer');
    return url.toString();
  }

  function replaceDrawer(html) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fresh = parsed.querySelector('[data-cart-drawer]');
    if (!fresh) return;
    var wasOpen = isOpen();
    if (drawer && drawer.parentNode) {
      drawer.parentNode.replaceChild(fresh, drawer);
    } else {
      body.appendChild(fresh);
    }
    drawer = fresh;
    if (wasOpen) {
      drawer.setAttribute('data-open', 'true');
      drawer.setAttribute('aria-hidden', 'false');
    }
  }

  function syncCount(cart) {
    var count = (cart && cart.item_count) || 0;
    all('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  var refreshSeq = 0;
  function refreshCart() {
    var seq = ++refreshSeq;
    refreshing = fetch(sectionUrl(), { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Section refresh failed');
        return response.json();
      })
      .then(function (data) {
        if (seq !== refreshSeq) return; // a newer refresh is in flight; let it win
        if (data && data['cart-drawer']) replaceDrawer(data['cart-drawer']);
      })
      .catch(function () { /* keep the existing markup on failure */ })
      .finally(function () { if (seq === refreshSeq) refreshing = null; });
    return refreshing;
  }

  function syncCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        syncCount(cart);
        emit('cart:updated', cart);
        return cart;
      })
      .catch(function () { return null; });
  }

  function clearError() {
    var el = drawer && one('[data-cart-error]', drawer);
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
  }

  function showError(message) {
    var el = drawer && one('[data-cart-error]', drawer);
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
  }

  /* ------------------------------------------------------------------ *
   * Mutations
   * ------------------------------------------------------------------ */
  function afterChange() {
    return refreshCart().then(syncCart);
  }

  function changeLine(key, quantity) {
    return jsonFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    });
  }

  function setLineBusy(line, busy) {
    if (!line) return;
    all('button, input', line).forEach(function (control) {
      control.disabled = busy;
    });
    line.classList.toggle('is-busy', busy);
  }

  function handleSubmit(form) {
    var submit = form.querySelector('[type="submit"]');
    if (submit) submit.disabled = true;
    form.classList.add('is-busy');
    clearError();
    jsonFetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    })
      .then(function () { return refreshCart(); })
      .then(function () {
        openCart();
        return syncCart();
      })
      .catch(function (err) {
        showError(err && (err.description || err.message));
      })
      .finally(function () {
        if (submit) submit.disabled = false;
        form.classList.remove('is-busy');
      });
  }

  /* ------------------------------------------------------------------ *
   * Events (all delegated on document so a refresh can't unbind them)
   * ------------------------------------------------------------------ */
  doc.addEventListener('cart:open', function () {
    openCart();
  });

  doc.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches) return;
    if (!(form.matches('form[action*="/cart/add"]') || form.hasAttribute('data-quick-add'))) return;
    event.preventDefault();
    handleSubmit(form);
  });

  doc.addEventListener('click', function (event) {
    var target = event.target;

    var closeBtn = target.closest('[data-cart-close]');
    if (closeBtn) { closeCart(); return; }

    var arBtn = target.closest('[data-ar-try-on]');
    if (arBtn) {
      event.preventDefault();
      emit('ar:open', { handle: arBtn.getAttribute('data-product-handle') });
      return;
    }

    var qtyDec = target.closest('[data-qty-decrease]');
    var qtyInc = target.closest('[data-qty-increase]');
    if (qtyDec || qtyInc) {
      var quickForm = target.closest('[data-quick-add]');
      var qtyInput = quickForm && one('[data-qty-input]', quickForm);
      if (qtyInput) {
        event.preventDefault();
        var value = parseInt(qtyInput.value, 10);
        if (isNaN(value)) value = 1;
        value = qtyInc ? value + 1 : value - 1;
        if (value < 1) value = 1;
        qtyInput.value = value;
      }
      return;
    }

    var decBtn = target.closest('[data-cart-line-decrease]');
    var incBtn = target.closest('[data-cart-line-increase]');
    var removeBtn = target.closest('[data-cart-line-remove]');
    if (decBtn || incBtn || removeBtn) {
      event.preventDefault();
      var line = target.closest('[data-line-key]');
      var key = line && line.getAttribute('data-line-key');
      var input = line && one('[data-cart-line-qty]', line);
      if (!key) return;
      var qty = input ? parseInt(input.value, 10) : 1;
      if (isNaN(qty)) qty = 1;
      var next;
      if (removeBtn) next = 0;
      else if (incBtn) next = qty + 1;
      else next = qty - 1;

      if (next < 0) next = 0;
      if (decBtn && next < 1) return; /* quantity 0 only via the remove button */

      setLineBusy(line, true);
      clearError();
      changeLine(key, next)
        .then(afterChange)
        .catch(function (err) { showError(err && (err.description || err.message)); })
        .finally(function () { if (line) setLineBusy(line, false); });
    }
  });

  doc.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches('[data-cart-line-qty]')) return;
    var key = input.getAttribute('data-line-key');
    var qty = parseInt(input.value, 10);
    if (!key || isNaN(qty) || qty < 0) return;
    var line = input.closest('[data-line-key]');
    if (qty === 0) { qty = 1; input.value = 1; }
    setLineBusy(line, true);
    changeLine(key, qty)
      .then(afterChange)
      .catch(function (err) { showError(err && (err.description || err.message)); })
      .finally(function () { if (line) setLineBusy(line, false); });
  });

  doc.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && isOpen()) { closeCart(); return; }
    if (event.key !== 'Tab' || !isOpen() || !drawer) return;
    var nodes = focusable(drawer);
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (event.shiftKey && doc.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && doc.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* ------------------------------------------------------------------ *
   * Public API for the PDP and quick-add
   * ------------------------------------------------------------------ */
  JT.cart = JT.cart || {};
  JT.cart.add = function (variantId, quantity) {
    var data = new FormData();
    data.append('id', variantId);
    data.append('quantity', quantity || 1);
    clearError();
    return jsonFetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: data
    })
      .then(afterChange)
      .then(function () { openCart(); })
      .catch(function (err) { showError(err && (err.description || err.message)); });
  };
  JT.cart.open = openCart;
  JT.cart.close = closeCart;
  JT.cart.refresh = refreshCart;
  JT.cart.money = money;
  window.JT = JT;
})();
